import type { DefaultDtoType, Field } from "../Field";
import { initField } from "../Field";
import type { InferOutput } from "valibot";
import { boolean, fallback, object, string } from "valibot";
import { dataName, label } from "../../utils/validation/base";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import { useFormServices } from "../../services/formServices";
import { TEXT_CONDITION_KEYS } from "../../constants";
import { defaultStringFilter } from "../../pages/customer/form/filter";
import { InputPreview, PreviewContentLabel } from "../FieldPreviewComponents";
import PropertySection from "../PropertySection";
import { Toggle } from "../../components/toggle/Toggle";
import { keyFromSelector } from "i18next";

const Schema = object({
  label_text: label(),
  data_name: dataName(),
  showheader: fallback(boolean(), false),
});
const ValueSchema = string();

export type ReadOnlyTextFieldProperties = InferOutput<typeof Schema>;
type ValueType = InferOutput<typeof ValueSchema>;

const ReadOnlyTextField: Field<ReadOnlyTextFieldProperties, DefaultDtoType, ValueType> = {
  type: "readOnlyText",
  category: "textFields",
  widget: "nl.gildesoftware:readonlytext:6",
  schema: Schema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => ({
    showheader: false,
  }),
  ruleBehaviour: {
    conditions: { keys: TEXT_CONDITION_KEYS, variant: "text" },
    actions: {
      variant: "text",
      setValue: {
        fieldTypes: [
          "address",
          "barcode",
          "checkbox",
          "calculation",
          "currentLocation",
          "date",
          "dateTime",
          "email",
          "iban",
          "licensePlate",
          "location",
          "lookup",
          "number",
          "phone",
          "price",
          "radio",
          "rating",
          "readOnlyText",
          "search",
          "slider",
          "smiley",
          "stopwatch",
          "text",
          "textArea",
          "time",
          "timeDifference",
        ],
        variableTypes: ["string", "number", "boolean", "date", "datetime"],
      },
    },
  },
  filter: {
    type: "string",
    definition: defaultStringFilter,
  },
  stringValue: (value) => value,
  renderValue: (value) => value,
  icon: "bookOpen",
  titleKey: keyFromSelector(($) => $.READ_ONLY_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.READ_ONLY_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    showheader: keyFromSelector(($) => $.FIELD_PROPERTY_SHOW_HEADER),
  },
  PreviewContent: (props) => (
    <PreviewContentLabel uid={props.uid} label={props.properties.showheader ? props.properties.label_text : undefined}>
      <InputPreview disabled />
    </PreviewContentLabel>
  ),
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService } = useFormServices();

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
            errorMessage={props.getError("label_text")}
            required
          />
          <Toggle
            block
            label={t(props.propertyKeys.showheader)}
            checked={props.data.properties.showheader}
            setChecked={(value) => props.setValue("showheader", value)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(ReadOnlyTextField);
